import React, { useCallback, useState, useEffect } from 'react';
import {
    CircularProgress,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    Button,
    withTheme,
    Tooltip
} from '@material-ui/core';
import moment from 'moment';
import { Alert } from '@material-ui/lab';
import { doSwaggerCall, useSwaggerClient } from '../../../../hooks/useSwaggerClient';
import { useNotify } from '../../../../hooks/useNotify';
import OneDocumentEdit from './OneDocumentEdit';
import AdminHeader from '../../../../components/atom/AdminHeader';
import { DeleteIcon, EditIcon, ErrorIcon, FileDownloadIcon, ArchiveIcon } from '../../../../icons';
import { MODAL_TYPE, useModals } from '../../../../hooks/useModal';

const DocumentEditor = ({ clientId }) => {
    const { notifyError, notifySuccess } = useNotify();
    const { showModal } = useModals();
    const [edited, setEdited] = useState(false);
    const [newform, setNewForm] = useState(false);
    const [outDatedDocuments, setOutDatedDocuments] = useState([]);
    const [documents, loading, docError, setDocuments] = useSwaggerClient(
        'clients',
        'getDocuments',
        {
            clientId
        }
    );

    const editFinished = useCallback(
        doc => {
            setDocuments(docs => {
                const old = docs.find(e => e.id === doc.id);
                if (old) {
                    return docs.map(e => (e.id === doc.id ? doc : e));
                }
                return [...docs, doc];
            });
            setEdited(false);
        },
        [setDocuments, setEdited]
    );

    const deleteDocument = useCallback(
        documentId => {
            const req = doSwaggerCall('documents', 'deleteDocument', {
                documentId
            });
            req.then(() => {
                // we finished, remove user from list
                notifySuccess('Document deleted');
                setDocuments(docs => docs.filter(u => u.id !== documentId));
            }).catch(() => {
                notifyError('Cannot delete activity');
            });
        },
        [setDocuments, notifyError, notifySuccess]
    );

    const archiveDocument = useCallback(
        document => {
            const req = doSwaggerCall('documents', 'updateDocument', {
                documentId: document.id,
                updatedDocument: {
                    type: document.type,
                    startDate: moment(document.startDate).format('YYYY-MM-DD'),
                    endDate: moment(document.endDate).format('YYYY-MM-DD'),
                    archive: true,
                    archivedAt: document.archived_at
                }
            });
            req.then(updatedDoc => {
                notifySuccess('Document archived');
                setDocuments(prevDocs =>
                    prevDocs.map(doc => (doc.id === updatedDoc.id ? updatedDoc : doc))
                );
            }).catch(() => {
                notifyError('Error while archiving document');
            });
        },
        [notifyError, notifySuccess, setDocuments]
    );

    useEffect(() => {
        const outdatedDocs =
            documents?.filter(
                doc =>
                    doc.archivedAt === null &&
                    (moment.utc(doc.endDate).isBefore(moment.utc()) ||
                        moment.utc(doc.startDate).isAfter(moment.utc()))
            ) || [];
        if (outdatedDocs.length > 0) {
            setOutDatedDocuments(outdatedDocs);
            return;
        }
        setOutDatedDocuments([]);
    }, [documents, setOutDatedDocuments]);

    if (docError) {
        return (
            <Grid container item xs={12}>
                <Alert severity="error">Could not load documents</Alert>
            </Grid>
        );
    }

    return (
        <Grid container spacing={2}>
            <AdminHeader title="Documents" />
            {(edited || newform) && (
                <OneDocumentEdit
                    editedDoc={edited}
                    editFinished={editFinished}
                    onClose={() => {
                        setNewForm(false);
                        setEdited(false);
                    }}
                />
            )}
            {!(edited || newform) && (
                <Grid item xs={12}>
                    {loading && documents?.length === 0 ? (
                        <CircularProgress />
                    ) : (
                        <>
                            {outDatedDocuments.length > 0 && (
                                <Grid item xs={12}>
                                    <Alert severity="error">
                                        Some documents are out of date / not yet valid!
                                    </Alert>
                                </Grid>
                            )}
                            <TableContainer className="mt-2">
                                <Table aria-label="documents-table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width={'5%'}></TableCell>
                                            <TableCell width={'15%'}>Type</TableCell>
                                            <TableCell width={'20%'}>Start date</TableCell>
                                            <TableCell width={'20%'}>End date</TableCell>
                                            <TableCell width={'20%'}>Archived</TableCell>
                                            <TableCell align="center" width={'20%'}>
                                                Actions
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {documents?.map(row => (
                                            <TableRow key={row.id}>
                                                <TableCell>
                                                    {outDatedDocuments.findIndex(
                                                        doc => doc.id === row.id
                                                    ) !== -1 && <ErrorIcon color="error" />}
                                                </TableCell>
                                                <TableCell component="th" scope="row">
                                                    {row.type}
                                                </TableCell>
                                                <TableCell>
                                                    {moment
                                                        .utc(row.startDate)
                                                        .format('MMM DD, YYYY')}
                                                </TableCell>
                                                <TableCell>
                                                    {moment.utc(row.endDate).format('MMM DD, YYYY')}
                                                </TableCell>
                                                <TableCell>
                                                    {row.archivedAt
                                                        ? moment
                                                              .utc(row.archivedAt)
                                                              .format('MMM DD, YYYY')
                                                        : '-'}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {!row.archivedAt && (
                                                        <Tooltip
                                                            title="Archive"
                                                            placement="bottom-end"
                                                        >
                                                            <IconButton
                                                                onClick={() => {
                                                                    archiveDocument(row);
                                                                }}
                                                            >
                                                                <ArchiveIcon color="secondary" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <IconButton
                                                        href={row.name}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <FileDownloadIcon color="secondary" />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={() => {
                                                            setEdited(row);
                                                        }}
                                                    >
                                                        <EditIcon color="primary" />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={() => {
                                                            showModal(MODAL_TYPE.CONFIRMATION, {
                                                                title: `Are you sure you want to delete this document?`,
                                                                content:
                                                                    'This action is irreversible.',
                                                                confirm: () =>
                                                                    deleteDocument(row.id)
                                                            });
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                    <TableFooter>
                                        <TableRow>
                                            <TableCell colSpan={6}>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={() => setNewForm(true)}
                                                >
                                                    Add new document
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </Grid>
            )}
        </Grid>
    );
};

export default withTheme(DocumentEditor);
